import { z } from 'zod';
/**
 * 0.17.0: Trace Admin's Health page (GET /v1/admin/health). platform-api reads DigitalOcean (App Platform, Insights
 * metrics and alerts, uptime checks) server-side and answers with what DigitalOcean returned, nothing more: every
 * number below is one DigitalOcean measured, and a value DigitalOcean did not return is null, never estimated. A read
 * that failed is named in `failures`, so the page can say which part is missing instead of showing a gap as healthy.
 */
/**
 * The two hosted environments, each one DigitalOcean project. Each Trace Admin shows its own environment's health only:
 * platform-api answers for the environment it runs in (hosted dev runs TRACE_ENV=staging and is dev here; development
 * and test read the local fake as dev).
 */
export const HEALTH_ENVIRONMENTS = ['production', 'dev'];
export const healthEnvironmentSchema = z.enum(HEALTH_ENVIRONMENTS);
/** The period the metrics, deploys and incidents cover. */
export const HEALTH_RANGES = ['24h', '7d', '30d'];
export const healthRangeSchema = z.enum(HEALTH_RANGES);
/**
 * GET /v1/admin/health query: the period, 24 hours unless asked otherwise (the environment is platform-api's own), and
 * whether this is a refresh of a page already open. Opening Health is audited once; the page's automatic refreshes
 * (every 30 seconds) and "Check again" send refresh=true and are not audited again: they show the same staff member
 * the same operational data they just opened. They are still staff only, MFA checked and rate limited.
 */
export const adminHealthQuerySchema = z.object({
    range: healthRangeSchema.default('24h'),
    refresh: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((value) => value === true || value === 'true')
        .default(false),
});
/**
 * Where the answer came from: DigitalOcean, the local fake (development and test only, synthetic data), or none (no
 * read-only DigitalOcean token in this environment, or no DigitalOcean project configured for it).
 */
export const HEALTH_SOURCES = ['digitalocean', 'fake', 'none'];
export const healthSourceSchema = z.enum(HEALTH_SOURCES);
/**
 * Why a p95 is missing although DigitalOcean reported one: too few requests in every step for a percentile to mean
 * anything (under about one a minute), so its histogram value is a bucket bound, not a duration.
 */
export const HEALTH_LATENCY_REASONS = ['not_enough_traffic'];
export const healthLatencyReasonSchema = z.enum(HEALTH_LATENCY_REASONS);
/** Why Health shows nothing: no token in this environment, or no DigitalOcean project for the asked environment. */
export const HEALTH_NOT_CONNECTED_REASONS = ['no_token', 'no_project'];
export const healthNotConnectedReasonSchema = z.enum(HEALTH_NOT_CONNECTED_REASONS);
/** An app as Health judges it from DigitalOcean's answers. unknown: DigitalOcean's answer for it is missing. */
export const HEALTH_APP_STATUSES = ['healthy', 'degraded', 'down', 'unknown'];
export const healthAppStatusSchema = z.enum(HEALTH_APP_STATUSES);
/** The parts of one read, so a failure names what is missing. */
export const HEALTH_READS = [
    'project',
    'app',
    'app_health',
    'deployments',
    'metrics',
    'uptime',
    'alerts',
];
export const healthReadSchema = z.enum(HEALTH_READS);
/** One read that failed: which part, of what (an app's name, a metric), and why in plain words (never a secret). */
export const healthFailureSchema = z.object({
    read: healthReadSchema,
    target: z.string().nullable(),
    message: z.string(),
});
/** One sample of a series: null where DigitalOcean had no value at that step (no traffic, no scrape). */
export const healthPointSchema = z.object({ at: z.iso.datetime(), value: z.number().nullable() });
/** A deployment's phase as DigitalOcean names it (apps_deployment_phase). */
export const HEALTH_DEPLOYMENT_PHASES = [
    'UNKNOWN',
    'PENDING_BUILD',
    'BUILDING',
    'PENDING_DEPLOY',
    'DEPLOYING',
    'ACTIVE',
    'SUPERSEDED',
    'ERROR',
    'CANCELED',
];
export const healthDeploymentPhaseSchema = z.enum(HEALTH_DEPLOYMENT_PHASES);
/** What became of a deploy: served (active or superseded since), failed, canceled, or still under way. */
export const HEALTH_DEPLOY_OUTCOMES = ['success', 'failed', 'canceled', 'in_progress'];
export const healthDeployOutcomeSchema = z.enum(HEALTH_DEPLOY_OUTCOMES);
export const healthDeploySchema = z.object({
    id: z.string(),
    phase: healthDeploymentPhaseSchema,
    outcome: healthDeployOutcomeSchema,
    /** DigitalOcean's own words for why it deployed (for example "app spec updated"). */
    cause: z.string().nullable(),
    createdAt: z.iso.datetime(),
    /** The commit deployed: the image tag's commit, or the source commit for apps built from Git. */
    commit: z.string().nullable(),
});
/** One component of an app (a service, worker or static site) as App Platform's health read reports it now. */
export const healthComponentSchema = z.object({
    name: z.string(),
    state: z.enum(['healthy', 'unhealthy', 'unknown']),
    replicasReady: z.number().int().nullable(),
    replicasDesired: z.number().int().nullable(),
    cpuPercent: z.number().nullable(),
    memoryPercent: z.number().nullable(),
});
/**
 * One app over the period (Insights): CPU, memory and requests are averages, the 5xx share is of all requests, and the
 * p95 latency is the peak. Null where DigitalOcean measured nothing.
 */
export const healthAppMetricsSchema = z.object({
    cpuPercent: z.number().nullable(),
    memoryPercent: z.number().nullable(),
    /**
     * Where CPU and memory come from: the period's averages from Insights, or, for an app Insights has no series for,
     * App Platform's component health now. Null when neither has a value.
     */
    resourcesSource: z.enum(['period_average', 'now']).nullable(),
    requestsPerSecond: z.number().nullable(),
    /** Share of requests answered 5xx, in percent; null without requests. */
    errorRatePercent: z.number().nullable(),
    /**
     * The peak of DigitalOcean's p95 request duration over the period, milliseconds (never an average of p95s), over
     * the steps with at least about one request a minute only.
     */
    latencyP95Ms: z.number().nullable(),
    /** The peak sits on DigitalOcean's top duration bucket (10 s): it means this or slower. */
    latencyP95AtTopBucket: z.boolean(),
    /** Why latencyP95Ms is null although DigitalOcean reported a p95. */
    latencyP95Reason: healthLatencyReasonSchema.nullable(),
});
/** The DigitalOcean uptime check that targets the app, when one exists. */
export const healthUptimeSchema = z.object({
    checkName: z.string(),
    /** DOWN in any region is down; checking while every region is still checking. */
    status: z.enum(['up', 'down', 'checking']).nullable(),
    /** DigitalOcean's 30-day uptime, averaged over the check's regions. */
    percent30d: z.number().nullable(),
});
/**
 * Series over the period, one point per step. The environment's (adminHealthSchema.series): apps averaged, requests
 * added up, latency the slowest app. One app's (healthAppSchema.series): that app's own values.
 */
export const healthSeriesSchema = z.object({
    cpuPercent: z.array(healthPointSchema),
    memoryPercent: z.array(healthPointSchema),
    requestsPerSecond: z.array(healthPointSchema),
    errorRatePercent: z.array(healthPointSchema),
    latencyP95Ms: z.array(healthPointSchema),
});
export const healthAppSchema = z.object({
    id: z.string(),
    /** DigitalOcean's name of the app (trace-platform-dev), shown as the secondary label. */
    name: z.string(),
    /**
     * The name people know it by: from the application registry (the app serving a registered product's host) or
     * platform-api's configured map of DigitalOcean app names; DigitalOcean's name when neither knows it.
     */
    displayName: z.string(),
    /** The app's live host (for example app.dev.trace.io), when DigitalOcean names one. */
    host: z.string().nullable(),
    status: healthAppStatusSchema,
    /** Plain words for anything that needs attention; empty when healthy. */
    reasons: z.array(z.string()),
    components: z.array(healthComponentSchema),
    /** The deployment serving now, or null when nothing is deployed. */
    deployment: healthDeploySchema.nullable(),
    /** A deployment under way right now. */
    inProgress: healthDeploySchema.nullable(),
    /** The latest deploys, newest first (at most ten). */
    recentDeploys: z.array(healthDeploySchema),
    uptime: healthUptimeSchema.nullable(),
    metrics: healthAppMetricsSchema,
    /**
     * The app's own series over the period (the Health charts' app filter), from the same Insights queries as the
     * environment's series (no extra DigitalOcean request); the p95 only over steps with enough traffic.
     */
    series: healthSeriesSchema,
    consoleUrl: z.string(),
});
/** A managed database or queue of the environment's project. */
export const healthServiceSchema = z.object({
    id: z.string(),
    kind: z.enum(['postgres', 'valkey', 'database']),
    /** DigitalOcean's cluster name is not read (that needs a scope that also reveals credentials): kind and short id. */
    name: z.string(),
    cpuPercent: z.number().nullable(),
    memoryPercent: z.number().nullable(),
    diskPercent: z.number().nullable(),
    /** Active connections (Postgres) or connected clients (Valkey), the latest value. */
    connections: z.number().nullable(),
    consoleUrl: z.string(),
});
/** One entry of the activity feed: a deploy or an incident (a DigitalOcean Insights alert). */
export const healthActivitySchema = z.object({
    id: z.string(),
    kind: z.enum(['deploy', 'incident']),
    at: z.iso.datetime(),
    title: z.string(),
    detail: z.string(),
    tone: z.enum(['success', 'attention', 'neutral']),
});
/**
 * The last few minutes across the environment's apps, for a near-real-time headline beside the period's figures. The 5xx
 * share and p95 need at least about one request a minute; below that `reason` is not_enough_traffic and they are null.
 */
export const healthRecentSchema = z.object({
    minutes: z.number().int(),
    requestsPerSecond: z.number().nullable(),
    errorRatePercent: z.number().nullable(),
    /** The slowest app's peak p95 over the minutes with enough traffic, milliseconds. */
    latencyP95Ms: z.number().nullable(),
    latencyP95AtTopBucket: z.boolean(),
    reason: healthLatencyReasonSchema.nullable(),
});
export const healthSummarySchema = z.object({
    appsTotal: z.number().int(),
    appsHealthy: z.number().int(),
    appsNeedingAttention: z.number().int(),
    deploysInRange: z.number().int(),
    failedDeploysInRange: z.number().int(),
    /** Average 30-day uptime of the uptime checks that target the environment's apps; null without any. */
    uptimePercent30d: z.number().nullable(),
    uptimeChecks: z.number().int(),
    requestsPerSecond: z.number().nullable(),
    errorRatePercent: z.number().nullable(),
    /** The peak p95 of the period: the slowest app at its worst step with enough traffic. */
    latencyP95Ms: z.number().nullable(),
    latencyP95AtTopBucket: z.boolean(),
    latencyP95Reason: healthLatencyReasonSchema.nullable(),
    activeIncidents: z.number().int(),
    /** The last five minutes (requests, 5xx share, p95), read on every answer. */
    recent: healthRecentSchema,
});
/**
 * One UTC day of an app's uptime history: the health-sample job's samples that day by outcome. Uptime is
 * (healthy + degraded) / samples: a degraded app still serves, so it counts as up (the page colours it "slow").
 */
export const healthUptimeDaySchema = z.object({
    day: z.iso.date(),
    samples: z.number().int(),
    healthy: z.number().int(),
    degraded: z.number().int(),
    down: z.number().int(),
    downMinutes: z.number().int(),
    /** The first reason the app was not healthy that day, in Health's words. */
    reason: z.string().nullable(),
});
/**
 * The environment's uptime history over the last `days` UTC days (from and to inclusive), one entry per app the job
 * sampled. A day without an entry has no data (before the app's first sample, or no sample taken).
 */
export const healthUptimeHistorySchema = z.object({
    from: z.iso.date(),
    to: z.iso.date(),
    days: z.number().int(),
    /** The sampling interval, minutes. */
    intervalMinutes: z.number().int(),
    apps: z.array(z.object({ appId: z.string(), appName: z.string(), days: z.array(healthUptimeDaySchema) })),
});
/** GET /v1/admin/health: one environment over one period. */
export const adminHealthSchema = z.object({
    environment: healthEnvironmentSchema,
    range: healthRangeSchema,
    source: healthSourceSchema,
    connected: z.boolean(),
    notConnectedReason: healthNotConnectedReasonSchema.nullable(),
    /** When platform-api read DigitalOcean (answers are cached for about 30 seconds). */
    checkedAt: z.iso.datetime(),
    window: z.object({ start: z.iso.datetime(), end: z.iso.datetime(), stepSeconds: z.number().int() }),
    /** The environment's DigitalOcean project in the control panel. */
    consoleUrl: z.string().nullable(),
    failures: z.array(healthFailureSchema),
    summary: healthSummarySchema,
    apps: z.array(healthAppSchema),
    series: healthSeriesSchema,
    services: z.array(healthServiceSchema),
    /** Deploys and incidents of the period, newest first (at most 30). */
    activity: z.array(healthActivitySchema),
    /** The last 90 days of sampled uptime per app (the page's uptime grid shows 30 or 90 of them). */
    uptimeHistory: healthUptimeHistorySchema,
});
//# sourceMappingURL=health.js.map